/**
 * The single source of truth for "who is logged in, with what
 * tokens/roles/permissions" (STEP 4/9). Persisted to `localStorage`
 * (not `sessionStorage`) deliberately — an enterprise desktop HMIS
 * user legitimately expects a browser refresh not to force re-login;
 * the real session boundary is the access token's own 15-minute
 * expiry (`JWT_ACCESS_EXPIRY`, `FRONTEND-BACKEND-INTEGRATION.md` §3)
 * plus this app's own idle-timeout logic (`hooks/useIdleTimer.ts`),
 * not tab lifetime.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser, TokenPair, DecodedAccessToken } from '@app-types/auth.types';

function decodeAccessToken(token: string): DecodedAccessToken | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as DecodedAccessToken;
  } catch {
    return null;
  }
}

interface AuthState {
  user: AuthUser | null;
  tokens: TokenPair | null;
  tenantId: string | null;
  roles: string[];
  permissions: string[];
  /** Sidebar-module visibility grants — see `core/permissions`' `hasModuleAccess` and `Sidebar.tsx`'s `isVisible()`. */
  moduleCodes: string[];
  isAuthenticated: boolean;
  setSession: (tokens: TokenPair, user: AuthUser) => void;
  setTokens: (tokens: TokenPair) => void;
  clearSession: () => void;
  isTokenExpired: () => boolean;
  /** True once the access token is within `leadMs` of its own `exp` — the signal to refresh BEFORE it lapses, rather than after. */
  isTokenExpiringWithin: (leadMs: number) => boolean;
}

/** `exp` is seconds since epoch; a token with no readable `exp` is treated as already gone. */
function tokenExpiryMs(token: string | undefined): number | null {
  if (!token) return null;
  const decoded = decodeAccessToken(token);
  if (!decoded?.exp) return null;
  return decoded.exp * 1000;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      tenantId: null,
      roles: [],
      permissions: [],
      moduleCodes: [],
      isAuthenticated: false,

      setSession: (tokens, user) => {
        const decoded = decodeAccessToken(tokens.accessToken);
        set({
          user,
          tokens,
          tenantId: decoded?.tenantId ?? null,
          roles: decoded?.roles ?? [],
          permissions: decoded?.permissions ?? [],
          moduleCodes: decoded?.moduleCodes ?? [],
          isAuthenticated: true,
        });
      },

      setTokens: (tokens) => {
        const decoded = decodeAccessToken(tokens.accessToken);
        set({
          tokens,
          tenantId: decoded?.tenantId ?? get().tenantId,
          roles: decoded?.roles ?? get().roles,
          permissions: decoded?.permissions ?? get().permissions,
          moduleCodes: decoded?.moduleCodes ?? get().moduleCodes,
        });
      },

      clearSession: () => {
        set({ user: null, tokens: null, tenantId: null, roles: [], permissions: [], moduleCodes: [], isAuthenticated: false });
      },

      isTokenExpired: () => {
        const expiry = tokenExpiryMs(get().tokens?.accessToken);
        if (expiry === null) return true;
        return expiry <= Date.now();
      },

      isTokenExpiringWithin: (leadMs) => {
        const expiry = tokenExpiryMs(get().tokens?.accessToken);
        if (expiry === null) return true;
        return expiry - leadMs <= Date.now();
      },
    }),
    {
      name: 'nexao-auth',
      partialize: (state) => ({ user: state.user, tokens: state.tokens }),
      onRehydrateStorage: () => (state) => {
        // Re-derive tenantId/roles/permissions/isAuthenticated from the persisted
        // token on load — only the minimal (user, tokens) pair is ever persisted,
        // everything else is a pure function of the token (never trust stale
        // persisted claims if the token itself is gone or expired).
        if (!state) return;

        const decoded = state.tokens?.accessToken ? decodeAccessToken(state.tokens.accessToken) : null;

        /**
         * AN EXPIRED ACCESS TOKEN IS NOT AN ENDED SESSION.
         *
         * This used to require a still-valid ACCESS token and otherwise wiped
         * `tokens` outright — throwing away a refresh token good for another
         * seven days. The access token lives fifteen minutes, so any reload
         * more than fifteen minutes after the last refresh (F5, a crash, a
         * reopened browser, a restored tab) silently destroyed a live session
         * and dumped the user at the login screen with work in progress.
         *
         * The session's real boundary is the REFRESH token. If that is still
         * valid we restore the session and let the refresh machinery mint a
         * new access token immediately; the stale claims below are only what
         * paints the first frame, and the server re-authorises every request
         * against the real token regardless. If the refresh token is gone or
         * itself expired, the session is genuinely over and we clear it.
         */
        const refreshExpiry = tokenExpiryMs(state.tokens?.refreshToken);
        const refreshTokenIsAlive = refreshExpiry !== null && refreshExpiry > Date.now();

        if (decoded && refreshTokenIsAlive) {
          state.tenantId = decoded.tenantId;
          state.roles = decoded.roles;
          state.permissions = decoded.permissions;
          state.moduleCodes = decoded.moduleCodes ?? [];
          state.isAuthenticated = true;
          return;
        }

        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
      },
    },
  ),
);
